import { Config } from '@environments/config';
import { Experiences, Style } from '@local/client-contracts';
import { AssistantsIconsConst, Constants } from '@local/common';
import {
  GENERAL_ASSISTANT_NAME,
  RFP_ASSISTANT_NAME,
  SALESFORCE_ASSISTANT_NAME,
  SLACK_ASSISTANT_NAME,
  TEAM_ASSISTANT_NAME,
  ZENDESK_ASSISTANT_NAME,
} from '@local/common-web';
import { OptionValue } from 'src/app/bar/views/collections-page/components/multi-choice-popup/multi-choice-popup.component';
import { AssistantBullet } from '../models/assistant-bullet.model';
import { WarningMessageModel } from '../models/filter-source-view-model';
import type { UiIconModel } from '@local/ui-infra';

type VisiblePermissionRole = Exclude<Experiences.PermissionRole, 'interact'>;

const WARNING_CONTENT: Record<Experiences.ExperienceType, Record<VisiblePermissionRole, string>> = {
  rfp: {
    creator: 'Only sources accessible to the selected identity can be added.',
    editor: 'Only sources accessible to the selected identity can be added.',
    viewer: null,
    sourceOwner: 'Private data sources configured for the bot will be visible as answers in the form',
  },
  slack: {
    creator: 'Only sources accessible to the selected identity can be used',
    editor: 'Only sources accessible to the selected identity can be used.',
    viewer: null,
    sourceOwner: 'Private data sources configured for the bot will be accessible in responses visible to all channel members.',
  },
  teams: {
    creator: 'Only sources accessible to the selected identity can be added.',
    editor: 'Only sources accessible to the selected identity can be added.',
    viewer: null,
    sourceOwner: 'Private data sources configured for the bot will be accessible in responses visible to all channel members.',
  },
  zendesk: {
    creator: 'For optimal bot functionality, ensure that the data sources you set are shared with your Unleash workspace.',
    editor: 'For optimal bot functionality, ensure that the data sources you set are shared with your Unleash workspace.',
    viewer: null,
    sourceOwner: 'For optimal bot functionality, ensure that the data sources you set are shared with your Unleash workspace.',
  },
  salesforce: {
    creator: 'For optimal bot functionality, ensure that the data sources you set are shared with your Unleash workspace.',
    editor: 'For optimal bot functionality, ensure that the data sources you set are shared with your Unleash workspace.',
    viewer: null,
    sourceOwner: 'For optimal bot functionality, ensure that the data sources you set are shared with your Unleash workspace.',
  },
  general: {
    creator: 'Only sources accessible to the selected identity can be added.',
    editor: 'Only sources accessible to the selected identity can be added.',
    viewer: null,
    sourceOwner: null,
  },
  agentic: {
    creator: 'Only sources accessible to the selected identity can be added.',
    editor: 'Only sources accessible to the selected identity can be added.',
    viewer: null,
    sourceOwner: null,
  },
};

const learnMoreContent: Record<Experiences.ExperienceType, string> = {
  slack: Constants.LEARN_MORE_SLACK_ASSISTANT,
  teams: Constants.LEARN_MORE_TEAMS_ASSISTANT,
  zendesk: Constants.LEARN_MORE_ZENDESK_ASSISTANT,
  rfp: Constants.LEARN_MORE_RFP_ASSISTANT,
  salesforce: Constants.LEARN_MORE_SALESFORCE_ASSISTANT,
  general: Constants.LEARN_MORE_GENERAL_ASSISTANT,
  agentic: Constants.LEARN_MORE_AGENTIC_ASSISTANT,
};

export const assistantContent = {
  created: 'created',
  updated: 'updated',
  headerInfo: {
    slack: 'Select the data sources the bot will use to answer questions asked in this channel.',
    zendesk: 'Choose the data sources that Unleash will use to generate answers and suggestions in Zendesk.',
    rfp: 'Select the knowledge base for auto-filling RFPs, security questionnaires, and more.',
    teams: 'Select the data sources the bot will use to answer questions asked in this channel.',
    salesforce: 'Choose the data sources that Unleash will use to generate answers and suggestions for Salesforce Cases.',
  },
  headerInfoReadonly: {
    slack: 'Answers to questions asked in this channel will be extracted from the following data sources',
    zendesk: 'Answers and suggestions provided within your Zendesk Suite are extracted from the following data sources',
    rfp: 'The knowledge bases selected for auto-filling RFPs, security questionnaires, and more.',
    teams: 'Answers to questions asked in this channel will be extracted from the following data sources',
    salesforce: 'Suggestions provided for your Salesforce cases are configured and extracted from the following data sources.',
  },
  learnMoreUrl: learnMoreContent,
  warningsContent: WARNING_CONTENT,
  preferences: 'Preferences',
  wikiCollections: 'Knowledge from Unleash',
  apps: 'Sources',
  access: 'Display Preferences',
  accessSubTitle: 'Control where this assistant appears for users with Interact permission.',
  deploymentInSlack: 'Deployment in Slack',
  deploymentInSlackSubTitle: 'Allow creating agent for a specific topic that users can chat with directly in Slack',
  deploymentInSlackTooltip: 'The deployment will be for all Slack workspace, usage according to assistant’s permissions.',
  deploymentInSlackButtonTooltip: 'It will be possible to deploy to Slack after creating and saving the assistant',
  conversationStarters: {
    title: 'Prompt Templates',
    subtitle: 'Guide users with editable prompt templates. Use brackets to mark customizable parameters (e.g. [project name]).',
    noPrompt: 'No prompts defined for this assistant',
    placeholder: 'Ask about [topic] or [project name]',
  },
  wikiCollectionInfo: {
    teams: 'The bot will use the data and files stored within the cards of the selected Wikis',
    slack: 'The bot will use the data and files stored within the cards of the selected Wikis',
    zendesk: 'The bot will use the data and files stored within the cards of the selected Wikis',
    rfp: 'Unleash will use the data and files stored within the cards of the selected Wikis',
    salesforce: 'The bot will use the data and files stored within the cards of the selected Wikis',
    general: 'The bot will use the data and files stored within the cards of the selected Wikis',
  },
  addQuery: 'Add data source',
  noAppsMessage: 'No data sources have been selected',
  noResults: 'No results',
  assistants: 'Assistants',
  addApp: 'Add source',
  remove: 'Remove',
  cancel: 'Cancel',
  save: 'Save',
  updateText: 'The changes have been saved',
  updateFailedText: 'Failed to save changes',
  createText: 'created successfully',
  assistantSubtitle: 'Create AI Assistants tailored for automated question answering, completing forms, and more.',
  learnMore: 'Learn more',
  newAssistant: 'New Assistant',
  disabledCreateButtonTooltip: 'Adding Assistants is limited to administrators of this workspace',
  accountNoAccessMessage: 'No access',
  knowledge: {
    knowledgeBase: {
      title: 'Knowledge Source',
      infoTooltip: '<b>Knowledge Base</b> <br/> Missing info text!.',
      dropdownOptions: [
        {
          label: 'Connected Sources',
          value: Experiences.KnowledgeType.Internal,
        },
        {
          label: 'Public Sources',
          value: Experiences.KnowledgeType.External,
          disabledTooltip: 'Contact your administrator',
        },
      ],
    },
    baseAIModelBase: {
      title: 'AI Model',
      infoTooltip: '<b>Base AI Model</b> <br/> Missing info text!.',
    },
    tools: {
      title: 'Tools',
      disabledTooltip: 'Tool is not supported by the selected model',
      allowWebAccess: 'Web search',
      allowWebAccessTooltip:
        "A web search is triggered for questions requiring time-sensitive information or specific details beyond the built-in LLM's knowledge base.",
      allowFileUpload: 'File search',
      allowFileUploadTooltip: 'Upload files to give the assistant more context. If disabled, it won’t search in uploaded files.',
    },
  },
  assistantDisclaimerLine1: 'The assistant will use the selected data sources to provide answers and search results, ensuring users only',
  assistantDisclaimerLine2: 'receive results from sources they have access to.',
  assistantFilesHeader: 'Files',
  noFilesMessage: 'No files have been uploaded',
  assistantFilesSubHeader: {
    general: 'Upload files for the assistant to use when answering questions. Files are visible to anyone with access to this assistant.',
    agentic:
      'Upload files for the assistant to reference when answering questions. Files are visible to everyone who has access to this assistant.',
  },
  toolsHeader: 'Tools',
  toolsSubHeader: 'Add tools to enrich the LLM with context and capabilities, enabling accurate answers and real task execution.',
  noToolsMessage: 'No tools have been selected',
  instructionsHeader: 'Instructions',
  instructionsSubHeader: "Provide clear instructions to customize the bot's response.",
  instructionsPlaceholder: 'Enter instructions for your assistant...',
  instructionsLearnMore: 'Learn More',
  assistantCustomTools: {
    header: 'New Custom Tool',
    description: 'Select how you want to configure your custom tool',
  },
  sourcesDescription:
    'Connect company data sources so the assistant can generate answers and search results securely, ensuring<br/>users only see results they can access.',
};

export const assistantBulletsContent: Record<Experiences.ExperienceType, AssistantBullet[]> = {
  slack: [
    {
      description: 'Require expert validation before publishing an answer',
      type: 'enableExpertsValidation',
      displayInfoIcon: true,
      infoTooltip:
        'When a user poses a question in a channel, Unleash will initially display the response exclusively to knowledge experts for approval or disapproval. The answer will become visible to everyone in the channel only upon the expert’s approval',
      options: [
        {
          description: 'Display "Awaiting Expert Approval" when review is pending',
          type: 'replyPendingMessage',
        },
        {
          type: 'showApprovalReaction',
          emoji: '2705',
          description: 'React with ',
          descriptionAfterEmoji: ' upon expert approval',
        },
        {
          type: 'showRejectReaction',
          emoji: '1F937-200D-2642-FE0F',
          description: 'React with',
          descriptionAfterEmoji: ' upon expert disapproval',
        },
      ],
    },
    {
      type: 'enableRelatedQuestions',
      icon: AssistantsIconsConst['slack'] as UiIconModel,
      description: 'Display related questions from selected Slack channels',
      actionInfo: {
        preText: 'Up to',
        postText: 'Questions',
      },
    },
    {
      type: 'enableRelatedArticles',
      description: 'Display related articles',
      actionInfo: {
        preText: 'Up to',
        postText: 'Articles',
      },
    },
    {
      type: 'startReaction',
      emoji: '1F440', //eyes
      description: 'React with',
      descriptionAfterEmoji: ' icon while searching for a suitable answer',
    },
    {
      type: 'endReaction',
      emoji: '1F937-200D-2642-FE0F', //shrug
      description: 'React with',
      descriptionAfterEmoji: ' icon when the bot can’t find an answer',
    },
    {
      type: 'feedbackReasoning',
      description: 'Specify reasons for negative feedback',
      displayInfoIcon: true,
      infoTooltip:
        'To enhance our algorithm, we encourage feedback on the bot’s generated answers. When negative feedback is received, we enable the user to provide a reason for the feedback, allowing us to better understand what went wrong.',
    },
    {
      type: 'actionButtonCustomization',
      description: 'Configure Action Buttons',
      displayInfoIcon: true,
      infoTooltip: 'See an example of how this section looks in Slack',
      panelActions: {
        inputs: [
          { text: 'Heading (optional)', placeholder: 'Type a heading' },
          { text: 'Description (optional)', placeholder: 'Type a description' },
        ],
      },
    },
  ],
  teams: [
    {
      type: 'enableRelatedArticles',
      description: 'Display related articles',
      actionInfo: {
        preText: 'Up to',
        postText: 'Articles',
      },
    },
    {
      type: 'feedbackReasoning',
      description: 'Specify reasons for negative feedback',
      displayInfoIcon: true,
      infoTooltip:
        'To enhance our algorithm, we encourage feedback on the bot’s generated answers. When negative feedback is received, we enable the user to provide a reason for the feedback, allowing us to better understand what went wrong.',
    },
  ],
  rfp: [],
  zendesk: [],
  salesforce: [],
  general: [], //TODO: add content of starter messages, access
  agentic: [], //TODO: add content of starter messages, access
};

export const filtersSourceActions = {
  privateLink: 'Private Link',
  priorityOn: 'Deprioritize',
  priorityOff: 'Click to prioritize answers from this source over others',
  priorityView: 'Answers from this source will be prioritized higher than other sources',
  priorityOffDisabled: 'Prioritizing requires at least two sources to be selected',
  noResultsInd: '0 Results',
  remove: 'Remove this data source',
};

export const filterSourceWarning: { [type: string]: WarningMessageModel } = {
  deletedLink: { text: 'Source removed. Delete and reconnect if necessary.', type: 'error' },
  staleLink: { text: 'Expired credentials. Reconnect or delete this source.', type: 'error' },
  noAccess: {
    text: 'This source has been deleted or isn’t shared with you.',
    type: 'warning',
  },
  sourceOwnerNoAccessForViewer: {
    text: 'The source isn’t accessible to the selected identity and won’t be used in answers.',
    type: 'error',
  },
  sourceOwnerNoAccessForEditor: {
    text: 'The source isn’t accessible to the selected identity. Remove it and add it again once shared.',
    type: 'error',
  },
  permissionsBased: {
    text: 'Permission-based source. Only the selected identity can edit this source.',
    type: 'warning',
  },
  sourceOwnerChangedPermissionsBased: {
    text: 'Permission-based source removed due to identity change.',
    type: 'error',
  },
  failedLink: { text: 'Sync failed, delete and reconnect the source.', type: 'error' },
  recentlyConnectedLink: { text: 'Full AI functionality will be available within 24 hours', type: 'warning' },
};

export const specificResourcePopupContent = {
  placeholder: 'Search for a specific resource',
  resourcesSelected: 'resources selected',
  cancel: 'Cancel',
  select: 'Select',
  clearAll: 'Clear all',
};

export const assistantsEmptyStateContent = {
  description: 'Optimize team efficiency by introducing Q&A bots and implementing automated form completions',
  assistantsApps: [
    {
      name: 'General Assistant',
      text: 'Integrate Unleash to elevate your search capabilities. Tailor the search experience to perfectly align with your needs, ensuring users always find relevant information effortlessly.',
      buttonText: 'Create New',
      icon: AssistantsIconsConst['general'] as UiIconModel,
      value: 'general',
      disabled: false,
    },
    {
      name: 'General Assistant (Next-Gen)',
      text: 'Go beyond knowledge retrieval with a tool driven, action oriented assistant. Empower your team to find answers, execute tasks, and deliver measurable impact - all within their daily workflow.',
      buttonText: 'Create New',
      icon: AssistantsIconsConst['general'] as UiIconModel,
      value: 'agentic',
      disabled: false,
      badge: 'Beta',
    },
    {
      name: SLACK_ASSISTANT_NAME,
      text: 'Transform your Slack channels with the Unleash Bot, which provides you with relevant answers drawn from your organizational knowledge.',
      buttonText: 'Create New',
      icon: AssistantsIconsConst['slack'] as UiIconModel,
      value: 'slack',
      disabled: false,
    },
    {
      name: TEAM_ASSISTANT_NAME,
      text: 'Level up your Microsoft Teams channels with the Unleash Bot, which delivers accurate answers to your questions sourced from your organization’s knowledge.',
      buttonText: Config.teamsInstallUrl ? 'Add to Teams' : 'Coming soon',
      icon: AssistantsIconsConst['teams'] as UiIconModel,
      disabled: Config.teamsInstallUrl ? false : true,
      value: 'teams',
    },
    {
      name: ZENDESK_ASSISTANT_NAME,
      text: 'Seamlessly integrate Unleash with Zendesk suite to empower agents with instant information, real-time coaching, and a unified knowledge hub.',
      buttonText: 'Add to Zendesk',
      icon: AssistantsIconsConst['zendesk'] as UiIconModel,
      value: 'zendesk',
      disabled: false,
    },
    {
      name: SALESFORCE_ASSISTANT_NAME,
      text: 'Resolve your Salesforce cases faster with the Unleash Bot, empowering your teams with instant insights for better customer support.',
      buttonText: 'Create New',
      icon: AssistantsIconsConst['salesforce'] as UiIconModel,
      value: 'salesforce',
      disabled: false,
    },
    {
      name: RFP_ASSISTANT_NAME,
      text: 'Seamlessly automate the completion of RFPs, security questionnaires, and other type of forms using your organizational knowledge.',
      buttonText: 'Create New',
      icon: AssistantsIconsConst['rfp'] as UiIconModel,
      value: 'rfp',
      disabled: false,
    },
  ],
};

export const addAssistantsContent: OptionValue[] = [
  {
    text: 'General Assistant',
    description: 'Customize your search experience in any platform',
    icon: AssistantsIconsConst['general'],
    value: 'general',
  },
  {
    text: 'General Assistant (Next-Gen)',
    description: 'Tool driven, action oriented, fully adaptable',
    icon: AssistantsIconsConst['general'],
    value: 'agentic',
    badge: { text: 'Beta', style: 'primary' },
  },
  {
    text: 'RFP Assistant',
    description: 'Automate RFPs, security questionnaires, and more',
    icon: AssistantsIconsConst['rfp'],
    value: 'RFP',
  },
  {
    text: 'Slack Channels Assistant',
    description: 'Automated answers for any Slack channel',
    icon: AssistantsIconsConst['slack'],
    value: 'slack',
    warningText: 'This feature has been disabled by an admin.<br/> Please contact them to enable it for you.',
  },
  {
    text: 'Teams Channels Assistant',
    description: Config.teamsInstallUrl ? 'Automated answers for any Teams channel' : 'Coming soon',
    icon: AssistantsIconsConst['teams'],
    value: 'teams',
    disabled: !Config.teamsInstallUrl,
  },
  {
    text: 'Zendesk Tickets Assistant',
    description: 'Automated responses for tickets in Zendesk',
    icon: AssistantsIconsConst['zendesk'],
    value: 'zendesk',
  },
  {
    text: 'Salesforce Cases Assistant',
    description: 'Automated responses for cases in Salesforce',
    icon: AssistantsIconsConst['salesforce'],
    value: 'salesforce',
  },
];

export const assistantFlags = ['salesforce', 'general', 'slack'];

export const botBehaviorContent = {
  header: 'Bot Behavior',
  botActivation: 'Bot Activation',
  excludedEmailsAndDomains: 'Exclude domains or emails',
  excludedEmailsAndDomainsTooltip:
    'Specify company domains or individual email addresses to prevent the bot from replying to users from these addresses.',
  excludedEmailsAndDomainsPlaceholder: 'Add domains or individual emails, separated by commas (e.g. company.com, user@company.com)',
  excludedEmailsAndDomainsError: 'One of the entries isn’t a valid domain or email',
  activateBotMessages: 'Allow responses to messages from other bots',
  optionsBotSensitivity: [
    {
      label: 'Every Message',
      subLabel: 'Receive responses to all questions',
      icon: { type: 'font-icon', value: 'icon-all-queries' },
      value: Experiences.BotSensitivity.All,
    },
    {
      label: 'Work Related Question',
      subLabel: 'Get answers only for work-related inquiries',
      icon: { type: 'font-icon', value: 'icon-professional' },
      value: Experiences.BotSensitivity.Professional,
    },
    {
      label: 'Explicitly Mentioned ',
      subLabel: 'Responses are provided only when the bot is mentioned',
      icon: AssistantsIconsConst.general,
      value: Experiences.BotSensitivity.Tagging,
    },
  ],
};

export const assistantFilesComponent = {
  errors: {
    maxFileSize: 'File size exceeds the maximum limit',
    invalidFileType: 'Unsupported file type',
    maximumFilesUpload: 'Maximum file limit reached',
    pendingFiles: 'File upload pending',
    failedFiles: 'Remove failed files to enable saving',
    beforeSaveDisabled: 'Uploading files is available after the assistant is created',
    modelNotSupportFileSearch: 'The selected model doesn’t support file tool',
  },
};
